import { useEffect, useState } from "react";
import { Outlet, useBlocker } from "react-router";
import { RouterProvider, type RouterProviderProps } from "react-router/dom";

import * as Sentry from "@sentry/react";

import Loading from "@components/Utility/Loading";
import { isAppStale } from "@features/appVersion/staleAppState";
import { sentryCreateBrowserRouter } from "@services/sentry";
import isMobile from "@utils/mobileDetect";

import { BootErrorFallback } from "./BootErrorFallback";
import { filterRoutesForAccess } from "./filterRoutesForAccess";
import { protectedRoutesHook } from "./protectedRoutesPluginHook";
import type { RouterTarget } from "./resolveRouterTarget";
import type { protectedRoute } from "./types";

export type RouteFactories = {
  createPublicRoutes(): protectedRoute[];
  createAuthenticatedRoutes(
    pluginRoutes: readonly protectedRoute[]
  ): protectedRoute[];
};

type Router = ReturnType<typeof sentryCreateBrowserRouter>;

type RouterMountState =
  | { status: "building" }
  | { status: "error"; error: unknown }
  | { status: "ready"; router: Router };

type RouterMountProps = Omit<RouterProviderProps, "router"> & {
  target: RouterTarget;
  routes: RouteFactories;
};

function StaleNavigationGuard() {
  const blocker = useBlocker(() => isAppStale());

  useEffect(() => {
    if (blocker.state === "blocked") {
      const { pathname, search, hash } = blocker.location;
      window.location.assign(pathname + search + hash);
    }
  }, [blocker]);

  return <Outlet />;
}

function createRouteConfig(
  target: RouterTarget,
  routes: RouteFactories
): protectedRoute[] {
  switch (target.kind) {
    case "public":
      return routes.createPublicRoutes();
    case "authenticated": {
      const pluginRoutes = protectedRoutesHook.compose(target.organization, {
        isMobile,
      });
      return filterRoutesForAccess(
        routes.createAuthenticatedRoutes(pluginRoutes),
        { role: target.role },
        target.permissions
      );
    }
    default: {
      const unreachable: never = target;
      return unreachable;
    }
  }
}

/**
 * Builds exactly one browser router for the target identity represented by
 * this keyed component instance.
 *
 * Freezing `target` and `routes` into `buildInputs` on mount is load-bearing:
 * it is what makes it safe for `RouterTarget` to carry freshly-allocated values
 * (the permission `Set`) that change identity on every parent render. Identity
 * churn cannot rebuild the router; only a new key, via `routerTargetKey`, can.
 */
export function RouterMount({
  target,
  routes,
  ...routerProviderProps
}: RouterMountProps) {
  const [buildInputs] = useState(() => ({ target, routes }));
  const [state, setState] = useState<RouterMountState>({
    status: "building",
  });

  useEffect(() => {
    let router: Router;

    try {
      const routeConfig = createRouteConfig(
        buildInputs.target,
        buildInputs.routes
      );
      router = sentryCreateBrowserRouter([
        { element: <StaleNavigationGuard />, children: routeConfig },
      ]);
    } catch (error: unknown) {
      console.error("Brandhub failed to load [router-build]", error);
      Sentry.captureException(error, {
        tags: { bootstrapSource: "router-build" },
      });
      setState({ status: "error", error });
      return;
    }

    setState({ status: "ready", router });
    return () => router.dispose();
  }, [buildInputs]);

  if (state.status === "building") {
    return <Loading />;
  }
  if (state.status === "error") {
    return <BootErrorFallback />;
  }

  return <RouterProvider router={state.router} {...routerProviderProps} />;
}
